<script setup lang="ts">
import { computed, useSlots } from 'vue';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppCheckboxGap,
  AppCheckboxSize,
  AppCheckboxTheme,
} from './AppCheckbox.model';

export interface AppCheckboxProps {
  checked: boolean;
  theme: AppCheckboxTheme;
  size: AppCheckboxSize;
  gap?: AppCheckboxGap;
  isDisabled?: boolean;
}
const props = withDefaults(defineProps<AppCheckboxProps>(), {
  isDisabled: false,
});

interface AppCheckboxEmits {
  (event: 'change', value: boolean): void;
}
const emit = defineEmits<AppCheckboxEmits>();

const slots = useSlots();
const hasTextSlot = computed(() => !!slots['default']);
const gap = computed(() => `var(--${props.gap ?? 'spacing-8'})`);

function handleChange(): void {
  emit('change', !props.checked);
}
</script>

<template>
  <label
    class="app-checkbox"
    :class="[props.size, props.theme, { disabled: props.isDisabled }]"
  >
    <div
      class="checkbox"
      :class="{ checked: props.checked, disabled: props.isDisabled }"
    >
      <input
        type="checkbox"
        :checked="props.checked"
        class="input"
        @change="handleChange"
      />
      <AppSvgIcon
        v-if="checked"
        name="check"
        :color="props.isDisabled ? 'color-secondary-90' : 'color-white'"
      />
    </div>
    <div v-if="hasTextSlot" class="label" :class="{ checked: props.checked }">
      <slot />
    </div>
  </label>
</template>

<style scoped lang="scss">
@import '../../styles/_media.scss';
@import '../../styles/_fonts.scss';
.app-checkbox {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: v-bind(gap);
  width: fit-content;
  &:not(.disabled) {
    @include on-hover {
      cursor: pointer;
    }
  }

  .checkbox {
    border-radius: var(--border-radius-8);
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;

    .input {
      display: none;
    }
  }

  .label {
    user-select: none;
  }

  // PROPS SETTINGS

  // // SIZE
  &.small {
    --small-checkbox-size: 26px;
    .checkbox {
      height: var(--small-checkbox-size);
      width: var(--small-checkbox-size);
    }
    .label {
      @include font-label-l-light;
    }
  }

  &.large {
    --large-checkbox-size: 30px;
    .checkbox {
      height: var(--large-checkbox-size);
      width: var(--large-checkbox-size);
    }
    .label {
      @include font-label-l-light;
    }
  }

  // // THEME && STATE
  &.secondary {
    .checkbox {
      background-color: var(--color-white);
      border: 2px solid var(--color-secondary-73);
      &.checked {
        border: none;
        background-color: var(--color-secondary-40);
      }

      &.disabled {
        background-color: var(--color-secondary-90);
        border: 2px solid var(--color-secondary-90);
        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-secondary-95);
        outline: 12px solid var(--color-secondary-95);
        border: 2px solid var(--color-secondary-40);
      }
    }
    .label {
      color: var(--color-secondary-60);
      &.checked {
        color: var(--color-secondary-40);
      }
    }
    .checkbox:focus-visible {
      background-color: var(--color-secondary-95);
      outline: 12px solid var(--color-secondary-95);
      border: 2px solid var(--color-secondary-40);
    }
  }
  &.grey {
    .checkbox {
      background-color: var(--color-secondary-95);
      &.checked {
        background-color: var(--color-primary-30);
      }

      &.disabled {
        background-color: var(--color-secondary-97);
        outline: 1px solid var(--color-secondary-93);
        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-secondary-93);
      }
    }
    .label {
      color: var(--color-secondary-60);
    }
  }

  &.grey-dark {
    .checkbox {
      background-color: var(--color-secondary-95);
      &.checked {
        background-color: var(--color-primary-30);
      }

      &.disabled {
        background-color: var(--color-secondary-97);
        outline: 1px solid var(--color-secondary-93);
        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-secondary-93);
      }
    }
    .label {
      color: var(--color-secondary-60);
      &.checked {
        color: var(--color-secondary-40);
      }
    }
  }

  &.white {
    .checkbox {
      background-color: var(--color-white);
      &.checked {
        background-color: var(--color-primary-20);
      }

      &.disabled {
        background-color: var(--color-white);
        outline: 1px solid var(--color-secondary-93);
        &.checked {
          outline: none;
        }
      }
    }
    @include on-hover {
      .checkbox:not(.checked):not(.disabled) {
        background-color: var(--color-secondary-93);
      }
    }
  }
  .label {
    color: var(--color-secondary-60);
  }
}
</style>
